import { describeMessageTrace } from './messageTraceCollapse.js';
import { describeNonInteractive } from './nonInteractiveRollup.js';
import { describeRolledRow } from './accessPeriods.js';
import { applyAllEventsRollups, rowAppLabel, signInApplicationLabel } from './allEventsRollups.js';
import { describeDeviceRegistration, dirAuditDeviceName } from './deviceRegistration.js';
import { describeQuarantineRelease } from './quarantineRelease.js';
import { CASE_ALERT_EVENT_TYPE, describeCaseAlert } from './caseAlerts.js';
/**
 * eventUtils.js — Shared event helper functions.
 *
 * Extracted from TimelineView.jsx so both the timeline UI and the story
 * generator can reuse the same logic without duplication.
 */

import ipaddr from 'ipaddr.js';
import { normalizeIsoUtc } from './timeFormat.js';

// ── Constants ────────────────────────────────────────────────────────────────

export const FILE_OPERATIONS = new Set([
  'FileAccessed', 'FilePreviewed', 'FileDownloaded', 'FileModified',
  'FileModifiedExtended', 'FileUploaded', 'FileMoved', 'FileRenamed',
  'FileDeleted', 'FileRecycled', 'FileCopied', 'FileCheckedOut', 'FileCheckedIn',
]);

export const LIST_OPERATIONS = new Set([
  'ListViewed', 'ListUpdated', 'ListColumnCreated', 'ListColumnUpdated',
]);

export const HIDDEN_EXCHANGE_OPS = new Set([
  'Send', 'SendAs', 'SendOnBehalf', 'MessageReadReceiptReceived', 'ReceiveEmail', 'MipLabel',
]);

export const HIDDEN_SHAREPOINT_OPS = new Set(['FileSyncUploadedFull', 'PagePrefetched']);

export const HIDDEN_DLP_OPS = new Set(['MipLabel']);

export const HIDDEN_GENERAL_OPS = new Set(['MessageReadReceiptReceived']);

// Noisy Microsoft mailbox-background operations that carry no user or attacker
// signal (e.g. Focused Inbox / Outlook priority-classification telemetry). Unlike
// the always-on HIDDEN_* sets above, these stay visible by default and are hidden
// only when the analyst turns on the "Outlook noise" toggle (mirrors MS-substrate).
export const OUTLOOK_NOISE_OPS = new Set(['OutlookItemPrioritized']);

// ── Helpers ──────────────────────────────────────────────────────────────────

export function parseBrowser(userAgent) {
  if (!userAgent) return 'Unknown';
  const ua = userAgent.toLowerCase();
  if (ua === 'bav2ropc') return 'Legacy Auth';
  if (ua.includes('teams/')) return 'Teams';
  if (ua.includes('outlook-ios') || ua.includes('outlook-android')) return 'Outlook Mobile';
  if (ua.includes('microsoft office')) return 'Office Desktop';
  if (ua.includes('edg/') || ua.includes('edge/')) return 'Edge';
  if (ua.includes('chrome/') && !ua.includes('edg/')) return 'Chrome';
  if (ua.includes('firefox/')) return 'Firefox';
  if (ua.includes('safari/') && !ua.includes('chrome/')) return 'Safari';
  if (ua.includes('python') || ua.includes('powershell')) return 'Script/API';
  return 'Other';
}

export function parseFolders(foldersStr) {
  if (!foldersStr) return null;
  try {
    return typeof foldersStr === 'string' ? JSON.parse(foldersStr) : foldersStr;
  } catch {
    return null;
  }
}

export function parseParameters(paramsStr) {
  if (!paramsStr) return null;
  try {
    const parsed = typeof paramsStr === 'string' ? JSON.parse(paramsStr) : paramsStr;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function getParam(params, name) {
  if (!params) return null;
  const p = params.find(p => p.Name?.toLowerCase() === name.toLowerCase() || p.name?.toLowerCase() === name.toLowerCase());
  return p ? (p.Value ?? p.value ?? null) : null;
}

/**
 * Derive the event-side device posture (OS / Intune compliant / Intune managed)
 * for the alert Baseline Comparison panel.
 *
 * The per-user baseline (silver_m365_7_day_normal / 30-day) is built exclusively
 * from graph_signin `deviceDetail`, so we prefer that same shape. Classic
 * Office365 Activity-Feed `signin` events (RecordType 15 UserLoggedIn) carry the
 * SAME facts in a `DeviceProperties` KV array instead, and the detection pipeline
 * never parses those — so without this fallback these rows render "—".
 *
 * Priority (first available source wins):
 *   1. the alert event's own graph `deviceDetail`
 *   2. the correlated graph_signin's `deviceDetail` (same source the baseline uses)
 *   3. the classic `DeviceProperties` KV array on the event itself
 *
 * Returns { os: string|null, compliant: bool|null, managed: bool|null }.
 * Compliant/managed are booleans (not "Yes"/"No") so the panel's matchBool can
 * compare them against the baseline's boolean normally_compliant / normally_managed.
 */
export function getSigninDeviceContext(rawEvent, correlatedSignin) {
  const fromDeviceDetail = (src) => {
    const dd = src?.deviceDetail;
    if (!dd || typeof dd !== 'object') return null;
    // A deviceDetail with no posture fields (e.g. only a browser) shouldn't block
    // the fallback — treat it as absent.
    if (dd.operatingSystem == null && dd.isCompliant == null && dd.isManaged == null) return null;
    const toBool = (v) => (typeof v === 'boolean' ? v : v == null ? null : String(v).toLowerCase() === 'true');
    return {
      os: dd.operatingSystem ?? null,
      compliant: toBool(dd.isCompliant),
      managed: toBool(dd.isManaged),
    };
  };

  // 1 & 2 — graph deviceDetail: the alert's own, else the correlated sign-in's.
  const graph = fromDeviceDetail(rawEvent) || fromDeviceDetail(correlatedSignin);
  if (graph) return graph;

  // 3 — classic Activity-Feed signin: DeviceProperties KV array.
  const dp = Array.isArray(rawEvent?.DeviceProperties) ? rawEvent.DeviceProperties : null;
  if (!dp) return { os: null, compliant: null, managed: null };

  const isTrue = (v) => v != null && String(v).toLowerCase() === 'true';
  const osVal = getParam(dp, 'OS');
  const compRaw = getParam(dp, 'IsCompliant');
  const cmRaw = getParam(dp, 'IsCompliantAndManaged');

  // Compliant is the direct analog of deviceDetail.isCompliant. If the standalone
  // IsCompliant prop is missing but the (compliant AND managed) conjunction is
  // true, the device is definitely compliant.
  const compliant = compRaw != null ? isTrue(compRaw) : (isTrue(cmRaw) ? true : null);

  // Managed has no standalone classic field — only the IsCompliantAndManaged
  // conjunction. Derive it precisely, never guessing:
  //   IsCompliantAndManaged=true                       → managed (both true)
  //   IsCompliant=true  & IsCompliantAndManaged=false  → NOT managed (compliant but not both)
  //   IsCompliant=false                                → unknown (can't isolate managed) → null → "—"
  const managed = isTrue(cmRaw) ? true : (isTrue(compRaw) ? false : null);

  return { os: osVal ?? null, compliant, managed };
}

/**
 * Hour-of-day (UTC, 0-23) of a sign-in event, or null.
 *
 * graph_signin uses `createdDateTime` (zone-aware); classic Activity-Feed
 * `signin` uses `CreationTime` (zone-LESS UTC). Both route through
 * normalizeIsoUtc so the zone-less classic value is treated as UTC and any
 * >3-digit fractional seconds are trimmed (Firefox/Safari reject them).
 */
export function getEventUtcHour(rawEvent) {
  const ts = rawEvent?.createdDateTime ?? rawEvent?.CreationTime ?? null;
  if (!ts) return null;
  const ms = Date.parse(normalizeIsoUtc(ts));
  return Number.isFinite(ms) ? new Date(ms).getUTCHours() : null;
}

/**
 * Canonical OS label for DISPLAY in the Baseline Comparison OS row, mirroring the
 * pipeline's normalize_os so the event value reads identically to the baseline
 * column (e.g. classic `DeviceProperties.OS` "MacOs" → "MacOS"; graph "Windows 10"
 * → "Windows"). Unrecognized values are returned unchanged (never collapsed to
 * "Unknown", so no detail is lost).
 */
export function canonicalOsLabel(s) {
  if (!s || typeof s !== 'string') return s;
  const n = s.toLowerCase();
  if (n.includes('windows')) return 'Windows';
  if (n.includes('mac') && !n.includes('machine')) return 'MacOS';
  if (n.includes('ios') || n.includes('iphone') || n.includes('ipad')) return 'iOS';
  if (n.includes('android')) return 'Android';
  if (n.includes('linux')) return 'Linux';
  return s;
}

/**
 * True if two IPs are on the SAME network — /24 for IPv4, /64 for IPv6. Same-kind
 * only; robust to IPv6 :: compression (compares raw bytes via ipaddr.js).
 */
export function ipSharesSubnet(a, b) {
  if (!a || !b) return false;
  try {
    const pa = ipaddr.parse(String(a).trim());
    const pb = ipaddr.parse(String(b).trim());
    if (pa.kind() !== pb.kind()) return false;
    const nBytes = pa.kind() === 'ipv6' ? 8 : 3;  // /64 or /24
    const ba = pa.toByteArray(), bb = pb.toByteArray();
    for (let i = 0; i < nBytes; i++) { if (ba[i] !== bb[i]) return false; }
    return true;
  } catch { return false; }
}

/**
 * Find a baseline IP on the SAME network as the event IP (/24 for IPv4, /64 for
 * IPv6) that is NOT an exact match — surfaces "this IP looks like it's on a network
 * the user normally signs in from" (e.g. event 102.129.232.177 vs baseline
 * 102.129.232.113, same /24). Returns the first such baseline IP, or null.
 */
export function findBaselineSubnetNeighbor(eventIp, signinIpListCsv) {
  if (!eventIp || !signinIpListCsv) return null;
  const evLower = String(eventIp).trim().toLowerCase();
  const list = signinIpListCsv.split(',').map(s => s.trim()).filter(Boolean);
  for (const b of list) {
    if (b.toLowerCase() === evLower) continue;  // exact match is handled separately
    if (ipSharesSubnet(eventIp, b)) return b;
  }
  return null;
}

export function summarizeInboxRule(params) {
  if (!params) return '';
  const parts = [];
  const name = getParam(params, 'Name');
  if (name) parts.push(`"${name}"`);
  const forwardTo = getParam(params, 'ForwardTo');
  const forwardAsAttachment = getParam(params, 'ForwardAsAttachmentTo');
  const redirectTo = getParam(params, 'RedirectTo');
  const deleteMessage = getParam(params, 'DeleteMessage');
  const moveToFolder = getParam(params, 'MoveToFolder');
  const markAsRead = getParam(params, 'MarkAsRead');
  if (forwardTo) parts.push(`forward to ${forwardTo}`);
  if (forwardAsAttachment) parts.push(`forward as attachment to ${forwardAsAttachment}`);
  if (redirectTo) parts.push(`redirect to ${redirectTo}`);
  if (deleteMessage === 'True' || deleteMessage === true) parts.push('delete message');
  if (moveToFolder) parts.push(`move to ${moveToFolder}`);
  if (markAsRead === 'True' || markAsRead === true) parts.push('mark as read');
  return parts.length > 0 ? ` — ${parts.join(', ')}` : '';
}

/**
 * Compute a diff between two parsed parameter arrays (previous vs current).
 */
export function computeParameterDiff(prevParams, currentParams) {
  if (!prevParams || !currentParams) return null;

  const prevMap = {};
  prevParams.forEach(p => { prevMap[(p.Name || p.name || '').toLowerCase()] = { name: p.Name || p.name, value: p.Value ?? p.value ?? '' }; });
  const currMap = {};
  currentParams.forEach(p => { currMap[(p.Name || p.name || '').toLowerCase()] = { name: p.Name || p.name, value: p.Value ?? p.value ?? '' }; });

  const allKeys = new Set([...Object.keys(prevMap), ...Object.keys(currMap)]);
  const diffs = [];

  for (const key of allKeys) {
    const prev = prevMap[key];
    const curr = currMap[key];
    const displayName = (curr || prev).name;
    const prevVal = prev ? String(prev.value) : '';
    const currVal = curr ? String(curr.value) : '';

    if (prevVal === currVal) {
      diffs.push({ name: displayName, type: 'unchanged', current: currVal });
      continue;
    }
    if (!prev) { diffs.push({ name: displayName, type: 'added_param', current: currVal }); continue; }
    if (!curr) { diffs.push({ name: displayName, type: 'removed_param', previous: prevVal }); continue; }

    const isList = prevVal.includes(';') || currVal.includes(';');
    if (isList) {
      const prevSet = new Set(prevVal.split(';').map(s => s.trim().toLowerCase()).filter(Boolean));
      const currSet = new Set(currVal.split(';').map(s => s.trim().toLowerCase()).filter(Boolean));
      const currItems = currVal.split(';').map(s => s.trim()).filter(Boolean);
      const prevItems = prevVal.split(';').map(s => s.trim()).filter(Boolean);
      const added = currItems.filter(x => !prevSet.has(x.toLowerCase()));
      const removed = prevItems.filter(x => !currSet.has(x.toLowerCase()));
      const unchangedCount = [...currSet].filter(x => prevSet.has(x)).length;
      diffs.push({ name: displayName, type: 'list', added, removed, unchangedCount });
    } else {
      diffs.push({ name: displayName, type: 'scalar', previous: prevVal, current: currVal });
    }
  }
  return diffs;
}

/**
 * Summarize parameter diff for description line.
 */
export function summarizeParamDiff(prevParams, currentParams) {
  const diff = computeParameterDiff(prevParams, currentParams);
  if (!diff) return '';
  const parts = [];
  for (const d of diff) {
    if (d.type === 'list' && (d.added.length > 0 || d.removed.length > 0)) {
      const shortName = d.name.replace(/SendersAndDomains$/, '').replace(/RecipientsAndDomains$/, 'Recip').replace(/^Blocked/, 'blocked').replace(/^Trusted/, 'trusted');
      const changes = [];
      if (d.added.length > 0) changes.push(`+${d.added.length}`);
      if (d.removed.length > 0) changes.push(`-${d.removed.length}`);
      parts.push(`${changes.join('/')} ${shortName}`);
    } else if (d.type === 'scalar') {
      parts.push(`${d.name}: ${d.previous} → ${d.current}`);
    }
  }
  return parts.length > 0 ? ` (${parts.join(', ')})` : '';
}

export function getMailAccessType(event) {
  const mat = event.MailAccessType || event.mailAccessType;
  const clientInfo = event.ClientInfo || '';
  if (mat && mat.toLowerCase() === 'sync') return 'Sync';
  if (!mat) {
    const actionMatch = clientInfo.match(/Action=([^;]+)/i);
    if (actionMatch && actionMatch[1].toLowerCase().includes('sync')) return 'Sync';
  }
  if (mat && mat.toLowerCase() === 'bind') {
    const serviceClients = /Client=(OutlookService|REST|MSExchangeRPC|ActiveSync|EWS|IMAP|POP)/i;
    if (serviceClients.test(clientInfo)) return 'AppAccess';
    return 'Read';
  }
  if (clientInfo) {
    const serviceClients = /Client=(OutlookService|REST|MSExchangeRPC|ActiveSync|EWS|IMAP|POP)/i;
    if (serviceClients.test(clientInfo)) return 'AppAccess';
    if (/Client=OWA/i.test(clientInfo)) return 'Read';
  }
  return null;
}

// ── Exchange / SharePoint category ───────────────────────────────────────────

export function getExchangeCategory(operation) {
  const fileOps = [
    'FileAccessed', 'FilePreviewed', 'FileDownloaded', 'FileModified',
    'FileModifiedExtended', 'FileUploaded', 'FileMoved', 'FileRenamed',
    'FileDeleted', 'FileRecycled', 'FileCopied', 'FileCheckedOut',
    'FileCheckedIn',
  ];
  const listOps = ['ListViewed', 'ListUpdated', 'ListColumnCreated', 'ListColumnUpdated'];
  if (fileOps.includes(operation)) return 'SharePoint';
  if (listOps.includes(operation)) return 'SharePointList';
  return 'Exchange';
}

// ── VPN / Proxy flags ────────────────────────────────────────────────────────

export function getVPNProxyFlags(event, ipLookup) {
  if (event._eventType === 'SignIn') {
    return {
      isVPN: event.IsVPN || event.RiskFactors?.IsVPN || false,
      isProxy: event.IsProxy || event.RiskFactors?.IsProxy || false,
      isTOR: event.IsTOR || event.RiskFactors?.IsTOR || false,
      isCompromised: event.IsCompromised || event.RiskFactors?.IsCompromised || false,
      isAnonymous: event.IsAnonymous || event.RiskFactors?.IsAnonymous || false,
    };
  }
  const ipInfo = ipLookup[event.IPAddress];
  if (ipInfo) {
    return {
      isVPN: ipInfo.IsVPN || false,
      isProxy: ipInfo.IsProxy || false,
      isTOR: ipInfo.IsTOR || false,
      isCompromised: ipInfo.IsCompromised || false,
      isAnonymous: ipInfo.IsAnonymous || false,
    };
  }
  return { isVPN: false, isProxy: false, isTOR: false, isCompromised: false, isAnonymous: false };
}

// ── Country lookup ───────────────────────────────────────────────────────────

export function getCountry(event, ipLookup) {
  if (event._eventType === 'SignIn') {
    return event.Location?.Country || event.IP_Country || '';
  }
  const ipInfo = ipLookup[event.IPAddress];
  if (ipInfo?.Country) return ipInfo.Country;
  if (!event.IPAddress && event.InferredIP) {
    const inferredInfo = ipLookup[event.InferredIP];
    return inferredInfo?.Country || '';
  }
  return '';
}

// ── Subject masking (potential PHI/PII) ─────────────────────────────────────
// Email subject lines can contain PHI/PII, so case views mask them by default
// behind a logged per-case reveal. These helpers are display-only — callers must
// keep the underlying data intact (search/filter, stored phishing flags).

export const SUBJECT_MASK = '•••••• (hidden)';
export const SUBJECT_HIDDEN_JSON = '(hidden — reveal to view)';
export const FILENAME_MASK = '•••••• (hidden)';

// Mask a single subject string unless revealed. Empty / falsy passes through so
// "(no subject)" style placeholders render unchanged.
export function maskSubject(subject, revealed) {
  if (!subject) return subject;
  return revealed ? subject : SUBJECT_MASK;
}

// Mask a file / attachment name unless revealed. The missing counterpart to
// maskSubject: until v1.151.0 no file-name mask existed anywhere in the app, so
// SharePoint/OneDrive document names and mail attachment names rendered verbatim
// in a case whose subjects were masked — including inside report timelines.
export function maskFileName(name, revealed) {
  if (!name) return name;
  return revealed ? name : FILENAME_MASK;
}

// ── The sensitive-field policy, in ONE place ────────────────────────────────
// Five consumers mask case-event detail (timeline description, timeline expanded
// row, Raw Logs render, Raw Logs clipboard, Demo Mode) and a sixth tokenizes it
// server-side (the executive report's redaction vocabulary). Every one of them
// used to carry its own idea of what was sensitive, and every new field therefore
// leaked until each was found separately. This list is the single definition; the
// cross-layer test in scripts/exchangeDescription.test.mjs enumerates it.
//
// `path` is documentation for the report-side vocabulary, which is path-keyed.
export const SENSITIVE_EVENT_FIELDS = [
  { field: 'Subject',           kind: 'subject',  path: '<event>.Subject' },
  { field: 'FlaggedSubject',    kind: 'subject',  path: 'relatedPhishingMessages[].FlaggedSubject' },
  { field: 'ItemSubject',       kind: 'subject',  path: 'exchangeEvents[].ItemSubject' },
  { field: 'ItemAttachments',   kind: 'filename', path: 'exchangeEvents[].ItemAttachments' },
  { field: 'FileName',          kind: 'filename', path: '<event>.Folders[].FileName' },
  { field: 'SourceFileName',    kind: 'filename', path: '<event>.Folders[].SourceFileName' },
  { field: 'FolderItems.Subject', kind: 'subject', path: '<event>.Folders[].FolderItems[].Subject' },
];

const SENSITIVE_SUBJECT_KEYS = new Set(
  SENSITIVE_EVENT_FIELDS.filter(f => f.kind === 'subject').map(f => f.field.split('.').pop())
);
const SENSITIVE_FILENAME_KEYS = new Set(
  SENSITIVE_EVENT_FIELDS.filter(f => f.kind === 'filename').map(f => f.field.split('.').pop())
);

// Recursively replace every sensitive string in a CLONED object with a
// placeholder, so masked detail can't leak through a raw-JSON viewer or its
// clipboard copy. Driven by SENSITIVE_EVENT_FIELDS — adding a field there covers
// this boundary automatically.
export function scrubSensitiveDeep(node) {
  if (Array.isArray(node)) { node.forEach(scrubSensitiveDeep); return node; }
  if (node && typeof node === 'object') {
    for (const k of Object.keys(node)) {
      const v = node[k];
      if (typeof v === 'string' && v) {
        if (SENSITIVE_SUBJECT_KEYS.has(k)) { node[k] = SUBJECT_HIDDEN_JSON; continue; }
        if (SENSITIVE_FILENAME_KEYS.has(k)) { node[k] = SUBJECT_HIDDEN_JSON; continue; }
      }
      scrubSensitiveDeep(v);
    }
  }
  return node;
}

// Retained name. sourceEvents/SourceEventsPanel.jsx keeps its own mirrored copy of
// the old subject-only walk, and the alert surfaces import this one — keeping the
// alias means the rename does not silently orphan either.
export const scrubSubjectsDeep = scrubSensitiveDeep;

// ── Exchange item-change description ─────────────────────────────────────────
// Exchange `Update` and `Create` had NO branch in either generateDescription copy,
// so they fell to the shared default and rendered as "performed Update" /
// "<app> performed Create" — a row the analyst is invited to dismiss, while the raw
// event named the modified property, the subject, the folder and the attachments.
//
// ONE exported helper, called from BOTH switches. The two copies of
// generateDescription have INVERTED flag polarity (eventUtils takes
// { maskSubjects }, TimelineView takes revealSubjects) and TimelineView's copy is not
// exported, so it cannot be unit-tested; a third inline copy of this logic would be
// untestable in the surface that matters. The helper therefore takes one explicit
// `revealDetails` boolean and each caller adapts its own polarity.

/** Attachment list parser. Format is `name (NNNNb)[; name (NNNNb)]`.
 *
 *  Microsoft documents `Item.Attachments` only as a string listing the names and
 *  sizes of attachments, with NO delimiter or escaping specified — so this is
 *  accept-or-reject rather than a bare split. Measured over 181,157 rows / 2 days /
 *  all tenants: 100% carry a `(NNNNb)` token, and splitting on "; " agrees with the
 *  token count while every segment ends in a size token in 99.79% of rows. On
 *  rejection we claim NO count: a wrong attachment count on an evidence row is worse
 *  than no count.
 *
 *  @returns {{names: string[], count: number}|null} null when unparseable. */
export function parseItemAttachments(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const segs = raw.split('; ');
  const names = [];
  for (const seg of segs) {
    const m = /^(.*?)\s*\((\d+)b\)$/.exec(seg);
    if (!m) return null;
    names.push(m[1]);
  }
  return { names, count: segs.length };
}

/** `\Inbox\THE LANDINGS\Attorneys` -> `\Inbox`.
 *  Nested folder paths carry client / matter names (54% of Calendar rows are
 *  nested), so only the TOP segment goes into a description; the full path stays in
 *  the expanded row. */
export function topFolderSegment(path) {
  if (!path || typeof path !== 'string') return '';
  const t = path.trim();
  if (!t) return '';
  const parts = t.split('\\').filter(Boolean);
  if (parts.length === 0) return t;
  return `\\${parts[0]}`;
}

/** ModifiedProperties -> array of property names, or null when absent/unusable.
 *  Accepts a JSON array string, an already-parsed array, or a bare string. */
export function parseModifiedProperties(raw) {
  if (!raw) return null;
  let val = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try { val = JSON.parse(t); } catch { return [t]; }
  }
  // A JSON-quoted SCALAR is one property, not "unusable". PowerShell's pipe form
  // unwraps a single-element array, so the runbook wrote '"AttachmentCollection"'
  // for the 98% of Update rows that change exactly one property. The runbook now
  // emits a real array, but accepting the scalar here ALSO repairs every blob
  // already written -- otherwise those cases need a full re-run to read correctly.
  if (typeof val === 'string') return val.trim() ? [val.trim()] : null;
  if (typeof val === 'number' || typeof val === 'boolean') return [String(val)];
  if (!Array.isArray(val)) return null;
  const names = val
    .map(v => (typeof v === 'string' ? v : (v && (v.Name || v.name)) || ''))
    .filter(Boolean);
  return names.length ? names : null;
}

// Security-relevant first, so the properties that indicate mail tampering surface
// ahead of body/formatting churn. AttachmentCollection appears on 127,606 rows/week
// across 26 tenants, RecipientCollection on 26,191 across 24.
const MODIFIED_PROP_PRIORITY = [
  'AttachmentCollection', 'RecipientCollection', 'MapiSubject', 'NormalizedSubjectInternal',
  'SenderEmailAddress', 'SentRepresentingEmailAddress', 'HtmlBody', 'TextBody', 'RtfBody',
];
const MODIFIED_PROP_DISPLAY_CAP = 3;
// A routine calendar save carries up to 117 modified properties. Without this
// collapse it read `changed AttachmentCollection, RecipientCollection, MapiSubject
// +52 more` — indistinguishable from mail tampering. Operator decision.
const CALENDAR_COLLAPSE_MIN_PROPS = 20;

const CREATE_FOLDER_WORDING = {
  drafts: 'saved a draft',
  calendar: 'created a calendar item',
  contacts: 'created a contact',
  notes: 'created a note',
  tasks: 'created a task',
};

function orderModifiedProps(names) {
  const rank = (n) => {
    const i = MODIFIED_PROP_PRIORITY.indexOf(n);
    return i === -1 ? MODIFIED_PROP_PRIORITY.length : i;
  };
  return [...names].sort((a, b) => rank(a) - rank(b));
}

/** The actor for an Exchange item-change row.
 *
 *  CallingAppName first: the value the row used to label "Application" was the
 *  top-level ApplicationId, which is the API PATHWAY rather than the caller on 26.6%
 *  of Exchange rows. On the reference case that made the attacker's own Update row
 *  render with an unresolvable bare GUID and no name. */
export function exchangeItemActor(event, upn) {
  return event.CallingAppName || event.ClientAppName || upn;
}

/**
 * Describe an Exchange `Update` / `Create` in terms of WHAT CHANGED.
 * Returns null when there is nothing better to say than today's wording, so the
 * caller falls back rather than emitting a bare "changed ".
 *
 * @param {object} event  the normalized Exchange event
 * @param {boolean} revealDetails  true = render subject detail verbatim
 * @param {string} upn  fallback actor when no app name is known
 */
export function describeExchangeItemChange(event, revealDetails, upn = 'Unknown user') {
  const rawOp = event.Operation || '';
  const op = (typeof rawOp === 'object') ? (rawOp.localizedValue || rawOp.value || '') : rawOp;
  if (op !== 'Update' && op !== 'Create') return null;

  const actor = exchangeItemActor(event, upn);
  const topFolder = topFolderSegment(event.ItemFolderPath);
  const folderKey = topFolder.replace(/^\\+/, '').toLowerCase();
  const props = parseModifiedProperties(event.ModifiedProperties);

  let verb;
  if (op === 'Create') {
    // Wording follows Microsoft's documented semantics for this operation: "An item
    // was created in the Calendar, Contacts, Draft, Notes, or Tasks folder ...
    // Creating, sending, or receiving a message isn't audited."
    verb = CREATE_FOLDER_WORDING[folderKey] || 'created an item';
  } else if (folderKey === 'calendar' && props && props.length >= CALENDAR_COLLAPSE_MIN_PROPS) {
    verb = 'saved a calendar item';
  } else if (props && props.length) {
    const ordered = orderModifiedProps(props);
    const shown = ordered.slice(0, MODIFIED_PROP_DISPLAY_CAP).join(', ');
    const extra = ordered.length - MODIFIED_PROP_DISPLAY_CAP;
    verb = `changed ${shown}${extra > 0 ? ` +${extra} more` : ''}`;
  } else {
    // ModifiedProperties absent / empty / malformed. Measured 0 of 152,881 real
    // Update rows, so this is defensive — but it must never render "changed ".
    return null;
  }

  const parts = [`${actor} ${verb}`];
  if (event.ItemSubject) parts.push(`on "${maskSubject(event.ItemSubject, revealDetails)}"`);
  if (topFolder) parts.push(`in ${topFolder}`);

  if (event.ItemAttachments) {
    // COUNT ONLY, and never the word "attached". Microsoft: Attachments is "a list of
    // the names and file size of ALL items that are attached to the message" — a
    // STATE, not a delta, so saying the actor attached something is a claim the data
    // does not support. Names never enter a description at all, which is also what
    // keeps them out of the executive report's prose.
    const n = (event.ItemAttachmentCount != null)
      ? event.ItemAttachmentCount
      : (parseItemAttachments(event.ItemAttachments)?.count ?? null);
    parts.push(n != null ? `(${n} attachment${n === 1 ? '' : 's'} present)` : '(attachments present)');
  }

  return parts.join(' ');
}

// ── Description generation ───────────────────────────────────────────────────

// Build a one-line description for a mailbox deletion-style operation
// (SoftDelete / HardDelete / MoveToDeletedItems), surfacing the affected mail
// item subjects. `folders` may be a single folder object or an array of them
// (real M365 data). Subjects are masked unless `revealSubjects` is true. Returns
// null when no affected items are present so the caller can fall back. Up to
// SUBJECT_DISPLAY_CAP subjects are listed; the rest are summarised as "+N more".
const SUBJECT_DISPLAY_CAP = 5;
export function describeDeletedMailItems(actor, verb, folders, revealSubjects, suffix = '') {
  const folderArr = Array.isArray(folders) ? folders : (folders ? [folders] : []);
  const items = folderArr.flatMap(f => (f && f.FolderItems) || []);
  if (items.length === 0) return null;
  const subjects = items.map(i => i.Subject).filter(Boolean);
  if (subjects.length === 0) {
    return `${actor} ${verb} ${items.length} item${items.length === 1 ? '' : 's'}${suffix}`;
  }
  const shown = subjects.slice(0, SUBJECT_DISPLAY_CAP).map(s => `'${maskSubject(s, revealSubjects)}'`).join(', ');
  if (subjects.length === 1) return `${actor} ${verb} ${shown}${suffix}`;
  const more = subjects.length > SUBJECT_DISPLAY_CAP ? ` (+${subjects.length - SUBJECT_DISPLAY_CAP} more)` : '';
  return `${actor} ${verb} ${subjects.length} items: ${shown}${more}${suffix}`;
}

export function generateDescription(event, opts = {}) {
  // A row produced by a story roll-up describes itself. ONE shared helper, because these
  // two copies of generateDescription have diverged before.
  const rolled = describeRolledRow(event);
  if (rolled) return rolled;

  const maskSubjects = !!opts.maskSubjects;
  const upn = event.UserPrincipalName || 'Unknown user';

  if (event._eventType === 'SignIn') {
    const op = event.Operation || 'SignIn';
    const app = signInApplicationLabel(event);
    const os = event.DeviceOS || 'Unknown';
    const browser = parseBrowser(event.UserAgent);
    const failed = event.ResultStatus && event.ResultStatus.toLowerCase() === 'failed' ? ' [FAILED]' : '';
    return `${op} for ${upn} on '${app}' (${os} / ${browser})${failed}`;
  }

  // Non-interactive sign-in activity: ONE ROW IS N AUTHENTICATIONS over a span within a
  // single hour, never a single event. Say so explicitly -- a reader who takes it for one
  // event will under-count token reuse by an order of magnitude.
  //
  // Outcome is stated because success-vs-failure is the CONTAINMENT signal in this feed
  // (on the reference case nothing succeeded after 2026-08-03, which is the point access
  // was cut off). Failure codes are named when present -- 50173 is a refresh-token
  // rejection, i.e. the revoke biting.
  //
  // Deliberately NOT said: anything attributing the activity to the attacker. The row
  // carries a session id, and a stolen token keeps the VICTIM's session id, so a shared
  // session is corroboration only. Attribution comes from the IP classification the
  // analyst applies, which colours the row through the normal mechanism.
  if (event._eventType === 'NonInteractiveSignIn') {
    // Delegates to the ONE shared helper (see describeNonInteractive) so a rolled row
    // cannot describe itself one way on screen and another in a report.
    return describeNonInteractive(event);
  }

  if (event._eventType === 'MessageTrace') {
    // ONE shared helper for both copies of generateDescription -- a collapsed row must
    // not describe itself one way in the timeline and another in a report.
    return describeMessageTrace(event, !maskSubjects, maskSubject);
  }

  if (event._eventType === 'UrlClick') {
    const action = event.ActionType || 'clicked';
    const url = event.Url || 'unknown URL';
    const truncUrl = url.length > 80 ? url.substring(0, 77) + '...' : url;
    const threat = event.ThreatType ? ` (${event.ThreatType})` : '';
    return `${action}: ${truncUrl}${threat}`;
  }

  if (event._eventType === 'AlertEvidence') {
    const url = event._alertUrl || getSecurityAlertUrl(event);
    if (url) {
      const truncUrl = url.length > 90 ? url.substring(0, 87) + '...' : url;
      return `Clicked suspicious URL: ${truncUrl}`;
    }
    return `User activity detected by MSFT Defender`;
  }

  if (event._eventType === 'SecurityAlert') {
    const title = event.Title || event.Operation || 'Security Alert';
    const severity = event.Severity ? `[${event.Severity.toUpperCase()}]` : '';
    const evidence = getSecurityAlertSummary(event);
    const truncEvidence = evidence && evidence.length > 80 ? evidence.substring(0, 77) + '...' : evidence;
    const parts = [severity, title];
    if (truncEvidence) parts.push(`— ${truncEvidence}`);
    return parts.filter(Boolean).join(' ');
  }

  if (event._eventType === CASE_ALERT_EVENT_TYPE) return describeCaseAlert(event);

  if (event._eventType === 'SecurityIncident') {
    const name = event.DisplayName || event.Operation || 'Security Incident';
    const severity = event.Severity ? `[${event.Severity.toUpperCase()}]` : '';
    return [severity, name].filter(Boolean).join(' ');
  }

  if (event._eventType === 'DirectoryAudit') {
    const op = event.Operation || 'unknown operation';
    const opLower = op.toLowerCase();
    const target = dirAuditTargetLabel(event);
    let detail = getDirAuditDetail(event);
    // Drop detail that merely restates the operation (e.g. passkey detail on a
    // "platform credential" op, where the action label already says "Passkey").
    const detailRedundant = detail && (
      detail.trim().toLowerCase() === opLower ||
      detail.trim().toLowerCase() === 'passkey (fido2)'
    );

    // "by <initiator>" — name who performed the action, but ONLY when it was
    // initiated by someone other than the account it acted on (admin/attacker
    // remediation or takeover). Self-service changes (initiator == target) read
    // cleaner without it. `upn` here is the initiator (the runbook resolves
    // object-id-only initiators to a UPN). Compare initiator vs target identity.
    const initId = (event.RawLog?.initiatedBy?.user?.id || '').toLowerCase();
    const initUpn = (event.RawLog?.initiatedBy?.user?.userPrincipalName || '').toLowerCase();
    const tgtId = (event.RawLog?.targetResources?.[0]?.id || '').toLowerCase();
    const tgtUpn = (event.RawLog?.targetResources?.[0]?.userPrincipalName || '').toLowerCase();
    const adminInitiated = (initId && tgtId && initId !== tgtId) || (initUpn && tgtUpn && initUpn !== tgtUpn);
    const by = (adminInitiated && upn && upn !== 'Unknown user') ? ` by ${upn}` : '';

    // Device registration / removal. Delegates to the ONE shared helper (see
    // describeDeviceRegistration) so this copy and TimelineView's cannot drift. Falls
    // through to the wording below when the row names no device.
    const deviceDesc = describeDeviceRegistration(event, target);
    if (deviceDesc) return deviceDesc;

    // Passkey / FIDO platform credential — Microsoft often logs these with the
    // initiator as an object-id only; the runbook resolves it to a UPN so `by`
    // can name the responder/attacker.
    if (opLower.includes('platform credential')) {
      const verb = opLower.startsWith('delete') ? 'Passkey removed' : 'Passkey registered';
      const dev = !detailRedundant && detail ? ` — ${detail}` : '';
      const forWhom = target ? ` for '${target}'` : '';
      return `${verb}${by}${forWhom}${dev}`;
    }

    // Produce descriptive labels for common security operations
    if (detail) {
      if (opLower.startsWith('update user') && detail) {
        return target ? `MFA device updated on '${target}'${by} — ${detail}` : `MFA device updated${by} — ${detail}`;
      }
      if (opLower.includes('deleted security info')) {
        return target ? `Security info removed${by} from '${target}' — ${detail}` : `Security info removed${by} — ${detail}`;
      }
      if (opLower.includes('registered security info')) {
        return target ? `Security info registered on '${target}'${by} — ${detail}` : `Security info registered${by} — ${detail}`;
      }
      if (opLower === 'disable account') {
        return target ? `Account disabled: '${target}'${by} — ${detail}` : `Account disabled${by} — ${detail}`;
      }
      if (opLower === 'enable account') {
        return target ? `Account enabled: '${target}'${by} — ${detail}` : `Account enabled${by} — ${detail}`;
      }
    }
    if (opLower.includes('started security info registration')) {
      return target ? `Security info registration started on '${target}'${by}` : `Security info registration started${by}`;
    }
    if (opLower.includes('changed default security info')) {
      return target ? `Default security info changed on '${target}'${by}` : `Default security info changed${by}`;
    }

    // Default: actor + operation + target + detail. Omit the "X performed"
    // prefix when no actor is known (service-initiated events).
    const hasActor = upn && upn !== 'Unknown user';
    const prefix = hasActor ? `${upn} performed ` : '';
    const base = target ? `${prefix}${op} on '${target}'` : `${prefix}${op}`;
    return detail && !detailRedundant ? `${base} — ${detail}` : base;
  }

  const rawOp = event.Operation || '';
  const op = (typeof rawOp === 'object') ? (rawOp.localizedValue || rawOp.value || '') : rawOp;
  const folders = parseFolders(event.Folders);
  const params = parseParameters(event.Parameters);
  const actor = event.ClientAppName || upn;

  switch (op) {
    case 'MailItemsAccessed': {
      const path = folders?.Path || '\\Inbox';
      const items = folders?.FolderItems;
      const accessType = getMailAccessType(event);
      const accessLabel = accessType === 'Sync' ? 'synced' : accessType === 'Read' ? 'read' : accessType === 'AppAccess' ? 'app-accessed' : 'accessed';
      if (items && items.length > 0) {
        const subjects = items.map(i => i.Subject).filter(Boolean);
        if (subjects.length === 1) return `${actor} ${accessLabel} '${maskSubject(subjects[0], !maskSubjects)}' in ${path}`;
        return `${actor} ${accessLabel} ${subjects.length} mail items in ${path}`;
      }
      return `${actor} ${accessLabel} mail items in ${path}`;
    }
    case 'New-InboxRule':
      return `${actor} created new inbox rule${summarizeInboxRule(params)}`;
    case 'Set-InboxRule':
      return `${actor} modified inbox rule${summarizeInboxRule(params)}`;
    case 'Remove-InboxRule':
      return `${actor} removed inbox rule${summarizeInboxRule(params)}`;
    case 'MoveToDeletedItems':
      return describeDeletedMailItems(actor, 'moved', folders, !maskSubjects, ' to Deleted Items')
        || `${actor} moved items to Deleted Items`;
    case 'SoftDelete':
      return describeDeletedMailItems(actor, 'soft-deleted', folders, !maskSubjects)
        || `${actor} soft-deleted mail items`;
    case 'HardDelete':
      return describeDeletedMailItems(actor, 'permanently deleted', folders, !maskSubjects)
        || `${actor} permanently deleted mail items`;
    case 'Send':
    case 'SendAs':
    case 'SendOnBehalf':
      return `${actor} sent email`;
    case 'Update':
    case 'Create':
      // Delegates to the ONE shared helper (see describeExchangeItemChange). Falls
      // back to today's wording rather than emitting a partial sentence.
      return describeExchangeItemChange(event, !maskSubjects, upn)
        || `${actor} performed ${op}`;
    case 'Release-QuarantineMessage':
    case 'QuarantineReleaseMessage':
      // The ONE shared helper, with THIS copy's polarity (`!maskSubjects` is "reveal").
      // Naming what was released is the whole finding: the generic default rendered the
      // most consequential user action in IR-20260902-tthomason-001 as
      // "performed Release-QuarantineMessage".
      return describeQuarantineRelease(event, !maskSubjects, maskSubject)
        || `${actor} performed ${op}`;
    case 'FileAccessed':
    case 'FilePreviewed': {
      // File names are masked with the same gate as subjects (v1.151.0). They were
      // passed RAW here, so a case whose subjects were masked still rendered customer
      // document names — in the timeline AND in the executive report's timeline
      // descriptions, which are built from this string.
      const fileName = maskFileName(folders?.FileName, !maskSubjects) || 'unknown file';
      const site = folders?.Path || '';
      const verb = op === 'FilePreviewed' ? 'previewed' : 'accessed';
      return site ? `${actor} ${verb} '${fileName}' on ${site}` : `${actor} ${verb} '${fileName}'`;
    }
    case 'FileDownloaded': {
      const fileName = maskFileName(folders?.FileName, !maskSubjects) || 'unknown file';
      return `${actor} downloaded '${fileName}'`;
    }
    case 'FileModified':
    case 'FileModifiedExtended': {
      const fileName = maskFileName(folders?.FileName, !maskSubjects) || 'unknown file';
      return `${actor} modified '${fileName}'`;
    }
    case 'FileUploaded': {
      const fileName = maskFileName(folders?.FileName, !maskSubjects) || 'unknown file';
      return `${actor} uploaded '${fileName}'`;
    }
    case 'FileMoved':
    case 'FileRenamed':
    case 'FileDeleted':
    case 'FileRecycled': {
      const fileName = maskFileName(folders?.FileName, !maskSubjects) || 'unknown file';
      const verb = op.replace('File', '').toLowerCase();
      return `${actor} ${verb} '${fileName}'`;
    }
    case 'ListViewed':
    case 'ListUpdated': {
      const path = folders?.Path || '';
      const listName = path.split('/').pop() || path || 'unknown list';
      const verb = op === 'ListViewed' ? 'viewed' : 'updated';
      return `${actor} ${verb} list '${listName}'`;
    }
    default: {
      if (op.startsWith('Set-') && event.PreviousParameters) {
        const prevP = parseParameters(event.PreviousParameters);
        if (prevP) {
          const diffSummary = summarizeParamDiff(prevP, params);
          return `${actor} performed ${op}${diffSummary}`;
        }
      }
      return `${actor} performed ${op || 'unknown operation'}`;
    }
  }
}

// ── Security Alert evidence extraction ────────────────────────────────────────

/** Extract the first URL from a Security Alert's evidence array or EvidenceUrls field. */
export function getSecurityAlertUrl(event) {
  // First check pre-extracted field (from runbook)
  if (event.EvidenceUrls) return event.EvidenceUrls.split(' | ')[0];

  // Fallback: parse evidence from RawLog
  const evidence = event.RawLog?.evidence;
  if (!Array.isArray(evidence)) return '';
  for (const ev of evidence) {
    if (ev.url) return ev.url;
    if (Array.isArray(ev.urls) && ev.urls.length > 0) return ev.urls[0];
  }
  return '';
}

/**
 * Return the single most relevant evidence string for a Security Alert.
 * Prefers URLs, then IP + verdict, then session info, then description snippet.
 */
export function getSecurityAlertSummary(event) {
  const evidence = event.RawLog?.evidence;
  const parts = [];

  // 1. URLs (most actionable)
  const url = getSecurityAlertUrl(event);
  if (url) parts.push(url);

  if (Array.isArray(evidence)) {
    // 2. IP with verdict
    for (const ev of evidence) {
      if (ev.ipAddress && ev.verdict && ev.verdict !== 'unknown') {
        const entry = `${ev.ipAddress} (${ev.verdict})`;
        if (!parts.includes(entry)) parts.push(entry);
      } else if (ev.ipAddress && !parts.some(p => p.includes(ev.ipAddress))) {
        parts.push(ev.ipAddress);
      }
    }
    // 3. Session info (browser/OS)
    for (const ev of evidence) {
      if (ev.browser || ev.operatingSystem) {
        const session = [ev.browser, ev.operatingSystem].filter(Boolean).join(' / ');
        if (session && !parts.includes(session)) parts.push(session);
        break;
      }
    }
  }

  // Fallback: IP from top-level field
  if (parts.length === 0 && event.IPAddress) {
    parts.push(event.IPAddress);
  }

  return parts.join(' | ');
}

/**
 * Extract a structured summary from Security Alert evidence for display.
 * Returns { urls, browser, os, ip, verdicts, mitre, alertWebUrl }.
 */
export function getSecurityAlertEvidence(event) {
  const result = {
    urls: [],
    browser: event.EvidenceBrowser || '',
    os: event.EvidenceOS || '',
    ip: event.IPAddress || '',
    verdicts: [],
    mitre: event.MitreTechniques || '',
    alertWebUrl: event.AlertWebUrl || '',
  };

  // Pre-extracted URLs from runbook
  if (event.EvidenceUrls) {
    result.urls = event.EvidenceUrls.split(' | ').filter(Boolean);
  }

  // Parse evidence from RawLog for richer detail
  const evidence = event.RawLog?.evidence;
  if (!Array.isArray(evidence)) return result;

  for (const ev of evidence) {
    // URLs
    if (ev.url && !result.urls.includes(ev.url)) result.urls.push(ev.url);
    if (Array.isArray(ev.urls)) {
      for (const u of ev.urls) {
        if (u && !result.urls.includes(u)) result.urls.push(u);
      }
    }
    // Session
    if (ev.browser && !result.browser) result.browser = ev.browser;
    if (ev.operatingSystem && !result.os) result.os = ev.operatingSystem;
    // IP
    if (ev.ipAddress && !result.ip) result.ip = ev.ipAddress;
    // Verdicts
    if (ev.verdict && ev.verdict !== 'unknown') {
      const type = (ev['@odata.type'] || '').replace('#microsoft.graph.security.', '').replace('Evidence', '');
      result.verdicts.push({ type, verdict: ev.verdict });
    }
  }

  // MITRE from RawLog
  if (!result.mitre && Array.isArray(event.RawLog?.mitreTechniques)) {
    result.mitre = event.RawLog.mitreTechniques.join(', ');
  }
  // Alert URL from RawLog
  if (!result.alertWebUrl && event.RawLog?.alertWebUrl) {
    result.alertWebUrl = event.RawLog.alertWebUrl;
  }

  return result;
}

// ── Directory Audit detail extraction ────────────────────────────────────────

/** Strip JSON-style quotes and brackets from Graph API property values. */
function cleanVal(v) {
  if (v == null) return '';
  let s = String(v).trim();
  // Remove surrounding [ ] or " "
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith('[') && s.endsWith(']'))) {
    s = s.slice(1, -1).trim();
  }
  // One more pass for nested quotes
  if (s.startsWith('"') && s.endsWith('"')) s = s.slice(1, -1).trim();
  return s;
}

/** Try to parse a JSON string value; returns null on failure. */
function tryParseJson(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s === '[]' || s === '""') return null;
  try {
    const parsed = JSON.parse(s);
    // Handle arrays — unwrap single-element arrays
    if (Array.isArray(parsed)) return parsed.length === 1 ? parsed[0] : parsed.length > 0 ? parsed : null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Deduplicate security alerts that are near-identical (same detection from
 * multiple Microsoft pipelines). Uses a composite key of Title +
 * FirstActivityDateTime + EvidenceUrls + IPAddress. Keeps the alert with
 * the earliest EventTimeUTC.
 */
export function deduplicateAlerts(alerts) {
  const seen = new Map();
  for (const a of alerts) {
    const key = [
      a.Title || '',
      a.FirstActivityDateTime || '',
      a.EvidenceUrls || '',
      a.IPAddress || '',
    ].join('|');
    const existing = seen.get(key);
    if (!existing || (a.EventTimeUTC || '') < (existing.EventTimeUTC || '')) {
      seen.set(key, a);
    }
  }
  return [...seen.values()];
}

/** Extract device name from a MsGraphPhoneAppDetail or similar JSON property value. */
function extractDeviceName(jsonVal) {
  if (!jsonVal || typeof jsonVal !== 'object') return '';
  return jsonVal.deviceName || jsonVal.DeviceName || jsonVal.displayName || jsonVal.DisplayName || '';
}

// (Bounds on "one action" moved to utils/directoryActions.js in v1.155.0, which groups
// by target+window instead of correlationId -- Microsoft splits one action across ids.)


/**
 * Name the Microsoft service that carried out a service-initiated directory audit.
 *
 * `ServiceInitiatedBy` is the runbook's answer to "whose IP is this then?" and is the
 * only one of the three that actually names a service: `ClientInfo` is a User-Agent
 * (`Microsoft Azure Graph Client Library 1.0` on a B2B invite), and the old literal
 * 'Azure ESTS' was correct only while ESTS was the sole case. Absent on every results
 * blob written before v1.152.0, so the fallbacks stay.
 */
export function serviceInitiatorLabel(event) {
  return event?.ServiceInitiatedBy || event?.ClientInfo || 'Azure ESTS';
}

/**
 * Identify the object a directory-audit event acted ON.
 *
 * `targetResources[0].displayName` is NULL on a whole family of user-lifecycle events —
 * B2B invitations among them — so the description fell through to a bare
 * "<actor> performed Update PasswordProfile" naming nothing that was changed. The UPN is
 * populated on those rows, so it is the fallback.
 *
 * Entra encodes an invited guest's UPN by replacing the last `@` of their real address
 * with `_` and appending `#EXT#@{tenant}.onmicrosoft.com`; that is decoded here, because
 * `sowmyatewari_gmail.com#EXT#@milmarcenturycorp.onmicrosoft.com` and
 * `sowmyatewari@gmail.com` are the same fact and only one of them reads as an external
 * mailbox at a glance. Decoding is anchored on the `#EXT#@` marker, so a UPN that merely
 * contains an underscore is untouched.
 */
export function dirAuditTargetLabel(event) {
  const tr = event?.RawLog?.targetResources?.[0];
  if (!tr) return '';
  if (tr.displayName) return tr.displayName;
  const upn = tr.userPrincipalName || '';
  const ext = upn.indexOf('#EXT#@');
  if (ext > 0) {
    const local = upn.slice(0, ext);
    const cut = local.lastIndexOf('_');
    if (cut > 0) return `${local.slice(0, cut)}@${local.slice(cut + 1)}`;
  }
  return upn;
}

/**
 * Extract a short human-readable detail string from a Directory Audit event's RawLog.
 * Returns empty string if nothing useful is found.
 */
export function getDirAuditDetail(event) {
  const raw = event?.RawLog;
  if (!raw) return '';

  const props = raw.targetResources?.[0]?.modifiedProperties;
  if (Array.isArray(props)) {
    for (const p of props) {
      const name = p.displayName || '';
      const newVal = cleanVal(p.newValue);
      const oldVal = cleanVal(p.oldValue);

      // MFA / security info — JSON-encoded device details
      // displayName is e.g. "MsGraphPhoneAppDetail" with JSON value containing deviceName
      if (name.startsWith('MsGraphPhoneAppDetail') || name.startsWith('StrongAuthentication')) {
        const parsed = tryParseJson(p.newValue) || tryParseJson(p.oldValue);
        const device = extractDeviceName(parsed);
        if (device) return device;
      }

      // Flat property: MsGraphPhoneAppDetail.deviceName (some tenants use dotted names)
      if (name === 'MsGraphPhoneAppDetail.deviceName') {
        if (newVal) return newVal;
        if (oldVal) return oldVal;
      }

      // FIDO2 keys
      if (name.startsWith('Fido2AuthenticationMethod')) {
        const parsed = tryParseJson(p.newValue) || tryParseJson(p.oldValue);
        const device = extractDeviceName(parsed);
        if (device) return device;
        if (newVal) return newVal;
        if (oldVal) return oldVal;
      }

      // Microsoft Authenticator app (e.g. "iPhone 14") — flat or JSON-encoded
      if (name.startsWith('MicrosoftAuthenticatorAuthenticationMethod')) {
        const parsed = tryParseJson(p.newValue) || tryParseJson(p.oldValue);
        const device = extractDeviceName(parsed);
        if (device) return device;
        if (newVal) return newVal;
        if (oldVal) return oldVal;
      }

      // FIDO passkey / platform credential — the device key carries usage=FIDO;
      // a friendly device name is rarely present, so label the method.
      if (name.startsWith('searchableDeviceKey') || name === 'KeyId') {
        const parsed = tryParseJson(p.newValue) || tryParseJson(p.oldValue);
        const device = extractDeviceName(parsed);
        if (device) return device;
        return 'passkey (FIDO2)';
      }

      // Email auth method
      if (name.startsWith('EmailAuthenticationMethod')) {
        const parsed = tryParseJson(p.newValue) || tryParseJson(p.oldValue);
        const email = parsed?.email || parsed?.Email || newVal || oldVal;
        if (email) return `email: ${email}`;
      }

      // Phone auth method
      if (name.startsWith('PhoneAuthenticationMethod')) {
        const parsed = tryParseJson(p.newValue) || tryParseJson(p.oldValue);
        const phone = parsed?.phoneNumber || parsed?.PhoneNumber || newVal || oldVal;
        if (phone) return `phone: ${phone}`;
      }

      // Account enabled/disabled
      if (name === 'AccountEnabled') {
        if (oldVal && newVal) return `AccountEnabled: ${oldVal} \u2192 ${newVal}`;
        if (newVal) return `AccountEnabled: ${newVal}`;
      }

      // Token revocation
      if (name === 'StsRefreshTokensValidFrom') return 'STS refresh tokens revoked';

      // Device registration / removal — the machine is the whole point of the row and
      // was being dropped before it reached the screen. See utils/deviceRegistration.js.
      if (name === 'Device.DisplayName') {
        const dev = dirAuditDeviceName(event);
        if (dev) return dev;
      }
    }
  }

  // App consent — target app name
  const targetName = raw.targetResources?.[0]?.displayName;
  const op = event.Operation || '';
  if (targetName && (op.includes('consent') || op.includes('Consent') || op.includes('permission') || op.includes('Permission') || op.includes('role assignment'))) {
    return targetName;
  }

  // Correlated device name from nearby "Update user" event
  if (event._correlatedDetail) return event._correlatedDetail;

  // Fallback: resultReason
  const reason = raw.resultReason;
  if (reason) {
    // Truncate long reasons
    return reason.length > 120 ? reason.substring(0, 117) + '...' : reason;
  }

  return '';
}

/**
 * Detect routine Azure MFA auto-updates that are not security-relevant.
 * These are "Update user" events initiated by "Azure MFA StrongAuthenticationService"
 * where both oldValue and newValue are populated (version bump, timestamp refresh)
 * and the device name hasn't changed — i.e. no add/remove, just maintenance.
 */
export function isAutoMfaUpdate(event) {
  const op = (event?.Operation || '').toLowerCase();
  if (!op.startsWith('update user')) return false;

  const raw = event?.RawLog;
  if (!raw) return false;

  // Must be initiated by a service principal (not a human)
  const app = raw.initiatedBy?.app;
  if (!app) return false;
  const appName = (app.displayName || '').toLowerCase();
  if (!appName.includes('strongauthentication') && !appName.includes('mfa')) return false;

  // Check StrongAuthenticationPhoneAppDetail — both old and new must be populated
  const props = raw.targetResources?.[0]?.modifiedProperties;
  if (!Array.isArray(props)) return false;

  for (const p of props) {
    const name = p.displayName || '';
    if (!name.startsWith('StrongAuthentication') && !name.startsWith('MsGraphPhoneAppDetail')) continue;

    const oldStr = (p.oldValue || '').trim();
    const newStr = (p.newValue || '').trim();
    const hasOld = oldStr && oldStr !== '[]' && oldStr !== '""';
    const hasNew = newStr && newStr !== '[]' && newStr !== '""';

    if (!hasOld || !hasNew) return false; // actual add or remove — keep it

    // Both populated — check if device name is the same (routine update)
    const oldParsed = tryParseJson(p.oldValue);
    const newParsed = tryParseJson(p.newValue);
    const oldDevice = extractDeviceName(oldParsed);
    const newDevice = extractDeviceName(newParsed);
    if (oldDevice && newDevice && oldDevice === newDevice) return true; // same device, just version/timestamp bump
    if (!oldDevice && !newDevice) return true; // can't determine device but both populated by MFA service
  }

  return false;
}

/** Check MFA-related modifiedProperties old/new values to determine add vs remove. */
function getMfaAction(event) {
  const props = event?.RawLog?.targetResources?.[0]?.modifiedProperties;
  if (!Array.isArray(props)) return null;
  for (const p of props) {
    const name = p.displayName || '';
    if (!name.startsWith('StrongAuthentication') && !name.startsWith('MsGraphPhoneAppDetail')) continue;
    const hasOld = p.oldValue && p.oldValue.trim() !== '' && p.oldValue.trim() !== '[]' && p.oldValue.trim() !== '""';
    const hasNew = p.newValue && p.newValue.trim() !== '' && p.newValue.trim() !== '[]' && p.newValue.trim() !== '""';
    if (hasNew && !hasOld) return 'MFA device added';
    if (hasOld && !hasNew) return 'MFA device removed';
    if (hasOld && hasNew) return 'MFA device changed';
  }
  return null;
}

/**
 * Return a more descriptive operation label for Directory Audit events.
 * Falls back to the raw Operation string when no specific mapping exists.
 * The `_detail` field (from getDirAuditDetail) is NOT repeated here — it
 * renders in its own column.
 */
export function getDisplayOperation(event) {
  const op = event?.Operation || '';
  const opLower = op.toLowerCase();
  const detail = event?._detail || '';

  // "Update user" with MFA device detail → determine add/remove from old/new values
  if (opLower.startsWith('update user') && detail) {
    const mfaAction = getMfaAction(event);
    if (mfaAction) return mfaAction;
    return 'MFA device updated';
  }

  // Security info operations — make them shorter / clearer
  if (opLower.includes('deleted security info')) return 'Security info removed';
  if (opLower.includes('registered security info') || opLower === 'register authentication method') return 'Security info registered';
  if (opLower.includes('required security info')) return 'Security info registered';
  if (opLower.includes('started security info registration')) return 'Security info registration started';
  if (opLower.includes('changed default security info')) return 'Default security info changed';

  // Passkey / FIDO platform credential
  if (opLower.includes('platform credential')) return opLower.startsWith('delete') ? 'Passkey removed' : 'Passkey added';

  // Account state
  if (opLower === 'disable account') return 'Account disabled';
  if (opLower === 'enable account') return 'Account enabled';

  // Token/password
  if (opLower.includes('stsrefreshtokenvalidfrom') || opLower.includes('sts refresh')) return 'Token revocation';
  if (opLower.includes('reset password') || opLower.includes('change password')) return 'Password reset';
  if (opLower === 'set force change password') return 'Force password change';

  // Consent / permissions
  if (opLower.includes('consent to application')) return 'App consent granted';
  if (opLower.includes('add delegated permission')) return 'Delegated permission added';
  if (opLower.includes('add app role assignment')) return 'App role assigned';

  // Group/role membership
  if (opLower === 'add member to group') return 'Added to group';
  if (opLower === 'remove member from group') return 'Removed from group';
  if (opLower === 'add member to role') return 'Role assigned';
  if (opLower === 'remove member from role') return 'Role removed';

  // Inbox rule
  if (opLower.includes('inbox rule')) return op;

  return op;
}

/**
 * Enrich directory audit events by correlating MFA registration events with
 * nearby "Update user" events that contain StrongAuthenticationPhoneAppDetail.
 *
 * Registration events ("Admin registered security info", etc.) don't carry
 * device details — those live on a companion "Update user" event within ~60s
 * on the same target user.  This function copies the device name across.
 *
 * Mutates `_correlatedDetail` on matching events; call BEFORE getDirAuditDetail.
 */
export function enrichDirAuditWithDeviceNames(events) {
  if (!events || events.length === 0) return events;

  const REGISTRATION_OPS = new Set([
    'Admin registered security info',
    'User registered security info',
    'User registered all required security info',
    'Register authentication method',
  ]);

  // Build index of "Update user" events with StrongAuthenticationPhoneAppDetail
  const updateUserDevices = [];
  for (const evt of events) {
    const op = evt.Operation || '';
    if (!op.toLowerCase().startsWith('update user')) continue;
    const props = evt.RawLog?.targetResources?.[0]?.modifiedProperties;
    if (!Array.isArray(props)) continue;
    for (const p of props) {
      if (p.displayName === 'StrongAuthenticationPhoneAppDetail') {
        const parsed = tryParseJson(p.newValue) || tryParseJson(p.oldValue);
        const device = extractDeviceName(parsed);
        if (device) {
          // Get target UPN from targetResources
          const targetUpn = (evt.RawLog?.targetResources?.[0]?.userPrincipalName || '').toLowerCase();
          updateUserDevices.push({
            time: evt.EventTimeUTC ? new Date(evt.EventTimeUTC).getTime() : 0,
            targetUpn,
            device,
          });
        }
        break;
      }
    }
  }

  if (updateUserDevices.length === 0) return events;

  // Correlate registration events with nearby "Update user" device events
  const WINDOW_MS = 120_000; // ±120 seconds
  for (const evt of events) {
    if (!REGISTRATION_OPS.has(evt.Operation)) continue;
    if (evt._correlatedDetail) continue; // already enriched
    const evtTime = evt.EventTimeUTC ? new Date(evt.EventTimeUTC).getTime() : 0;
    if (!evtTime) continue;
    // Target UPN from targetResources on the registration event
    const targetUpn = (evt.RawLog?.targetResources?.[0]?.userPrincipalName || '').toLowerCase();

    let bestMatch = null;
    let bestDelta = Infinity;
    for (const ud of updateUserDevices) {
      const delta = Math.abs(evtTime - ud.time);
      if (delta > WINDOW_MS) continue;
      // Must match target user
      if (targetUpn && ud.targetUpn && targetUpn !== ud.targetUpn) continue;
      if (delta < bestDelta) {
        bestDelta = delta;
        bestMatch = ud;
      }
    }
    if (bestMatch) {
      evt._correlatedDetail = bestMatch.device;
    }
  }

  return events;
}

// ── IP classification (replicated from TimelineView classifyIP) ──────────────

export function classifyIP(ip, safeIPSet, ipLookup, ipClassifications, event) {
  if (ipClassifications) {
    const explicit = ipClassifications[ip];
    if (explicit && explicit !== 'default') return explicit;
  }
  if (safeIPSet && safeIPSet.has(ip)) return 'safe';
  const info = ipLookup && ipLookup[ip];
  if (info) {
    const tier = info.AzureServiceTier || info.azureServiceTier || '';
    if (tier === 'MS-Backend' || tier === 'MS-Shared-Infra') {
      if (event) {
        const et = event._eventType || '';
        const isProxyType = et === 'SharePoint' || et === 'SharePointList' || et === 'Exchange';
        if (isProxyType) {
          const method = event.IPResolutionMethod || '';
          const resolved = method === 'SessionIPMap' || method === 'SessionIPMap-MultiIP'
            // TimeWindowCorrelation and single UserIPCorrelation are GUESSES (utils/guessedIp.js):
            // the source is unknown, so the recorded Microsoft address must not read as safe.
            || method === 'UserIPCorrelation-MultiIP';
          if (!resolved) return 'default';
        }
      }
      return 'ms-infra';
    }
    if (tier === 'User-Compute') return 'ms-compute';
  }
  return 'default';
}

// ── IP lookup builder ────────────────────────────────────────────────────────

export function buildIpLookup(ipAddresses) {
  const lookup = {};
  if (ipAddresses) {
    ipAddresses.forEach(ip => { lookup[ip.IPAddress] = ip; });
  }
  return lookup;
}

// ── Implicit-trust "must show" exception ─────────────────────────────────────

/**
 * Returns true when an event must stay visible despite belonging to an
 * implicitly-trusted class (a known-safe "Safe app" OR an `MS-Substrate` read),
 * because it carries a threat signal an analyst must never lose. Single source of
 * truth shared by the timeline filter (TimelineView) and the report generator
 * (combineAndTagEvents) so the two can never diverge (Design 2026-06-15 §6.3).
 *
 * Checks, across every IP the event touches (IPAddress, ResolvedUserIP, InferredIP,
 * and each comma-separated AllResolvedIPs value):
 *   - analyst classification of 'suspect' | 'malicious'
 *   - IP-enrichment threat flags (VPN / Proxy / TOR / compromised / anonymous)
 * plus the event-level precomputed `_vpnProxyFlags`, plus a compromised-session
 * override when the event carries a session id present in `compromisedSessions`.
 *
 * Degrades gracefully: an omitted ipLookup / ipClassifications / compromisedSessions
 * simply skips that check. MS-Substrate events are sessionless by construction, so
 * the session override is a no-op for them and the IP/threat checks carry the weight.
 *
 * @param {object} evt The enriched event.
 * @param {object} opts
 * @param {Object<string,string>} [opts.ipClassifications] ip -> 'safe'|'suspect'|'malicious'.
 * @param {Object<string,object>} [opts.ipLookup] ip -> enrichment record (IsVPN/IsProxy/...).
 * @param {Set|Map|null} [opts.compromisedSessions] session ids flagged compromised.
 * @returns {boolean}
 */
export function mustShowDespiteImplicitTrust(evt, { ipClassifications = {}, ipLookup = {}, compromisedSessions = null } = {}) {
  if (!evt) return false;

  // Collect every IP the event is associated with.
  const ips = [];
  if (evt.IPAddress) ips.push(evt.IPAddress);
  if (evt.ResolvedUserIP) ips.push(evt.ResolvedUserIP);
  if (evt.InferredIP) ips.push(evt.InferredIP);
  if (evt.AllResolvedIPs) {
    String(evt.AllResolvedIPs).split(',').forEach(ip => {
      const t = ip.trim();
      if (t) ips.push(t);
    });
  }

  for (const ip of ips) {
    const cls = ipClassifications[ip];
    if (cls === 'suspect' || cls === 'malicious') return true;
    const info = ipLookup[ip];
    if (info && (info.IsVPN || info.IsProxy || info.IsTOR || info.IsCompromised || info.IsAnonymous)) return true;
  }

  // Event-level precomputed flags (covers SignIn events; safety net elsewhere).
  const f = evt._vpnProxyFlags;
  if (f && (f.isVPN || f.isProxy || f.isTOR || f.isCompromised || f.isAnonymous)) return true;

  // Compromised-session override (moot for sessionless substrate; defense-in-depth otherwise).
  if (compromisedSessions) {
    const sid = evt.SessionId || evt.AADSessionId;
    if (sid && typeof compromisedSessions.has === 'function' && compromisedSessions.has(sid)) return true;
  }

  return false;
}


/**
 * Did this run actually READ the non-interactive sign-in feed?
 *
 * 🔴 A DIFFERENT FACT FROM WHETHER IT HELD ANYTHING, and the array cannot carry both.
 * `normalizeResults` defaults `NonInteractiveSignInEvents` to `[]`, so a run from before
 * v1.149.0 (no such key) and a run where the feed was queried and came back empty look
 * identical downstream. `CaseScopeNotice` has to tell them apart: on the first the timeline
 * really is incomplete, on the second it is not — and it asserted "not included in these
 * results" on both until v1.174.0, including on a case whose blob carried 172 rows.
 *
 * Reads the KEY's presence on the raw blob, before any default is applied. A run whose
 * query FAILED counts as NOT collected even though the key exists: the array is empty for
 * a reason worth disclosing, and over-disclosing on a failure is the safe direction.
 *
 * @param {object} blob The raw investigation results blob (camelCase or PascalCase).
 * @returns {boolean}
 */
export function nonInteractiveCollected(blob) {
  if (!blob || typeof blob !== 'object') return false;
  const has = Object.prototype.hasOwnProperty.call(blob, 'nonInteractiveSignInEvents')
    || Object.prototype.hasOwnProperty.call(blob, 'NonInteractiveSignInEvents');
  if (!has) return false;
  const errs = blob.dataSourceErrors || blob.DataSourceErrors || [];
  const failed = (Array.isArray(errs) ? errs : [])
    .some((d) => String((d && (d.workload || d.Workload)) || '') === 'graph_signin_noninteractive');
  return !failed;
}

// ── The two per-workload SELECTION rules, in ONE place ───────────────────────
//
// 🔴 v1.156.0 unified the ROLL-UPS between the screen and the reports and left these
// behind, so the two paths still chose different ROWS: the screen kept a phishing-flagged
// inbound message and showed only the confirmed URL click, while the report stream did
// neither. That is the same class of drift v1.156.0 existed to remove, and it had a
// second consequence -- `reviewedEventCount` counted records that no view of the case
// can display, so a report said "167 events were incident related" over a screen showing
// 43 rows, 83 of the difference being ordinary inbound mail that no pill can reach
// (IR-20260826-gwoodward-001).
//
// A pill is how an analyst says "this is not the attacker's story". A record the timeline
// never renders is outside that instrument entirely -- it can be neither eliminated nor
// affirmed -- so it must not be counted as incident-related by default. These two
// functions are therefore the definition of "in the analyst's final view", and both the
// screen and every report call them.

/**
 * Message-trace rows the timeline shows: outbound mail, plus any inbound message the
 * analyst has flagged as a phishing entry point.
 *
 * 🔴 Inbound mail is deliberately NOT in the timeline -- it lives on its own tab, where
 * the analyst's act is the phishing flag. So a flagged message IS incident-related and
 * the rest of the delivery volume is not: on the reference case that is 1 of 83, and
 * counting all 83 asserted an attacker relationship the records cannot carry (none of
 * the 129 raw records held the malicious IP, and none held a session id at all).
 *
 * @param {Array<object>} messageTraceEvents Filtered message-trace rows.
 * @param {Array<{eventId: string}>} phishingFlags The case's phishing flags.
 * @returns {Array<object>} Tagged rows, pre-collapse (callers collapse per-recipient).
 */
export function selectTimelineMessageTrace(messageTraceEvents, phishingFlags) {
  const flagged = new Set((phishingFlags || []).map((f) => f && f.eventId).filter(Boolean));
  return (messageTraceEvents || [])
    .filter((evt) => evt.Direction === 'Sent' || flagged.has(evt.EventId))
    .map((evt) => ({
      ...evt,
      _eventType: 'MessageTrace',
      _category: 'MessageTrace',
      UserPrincipalName: evt.SenderAddress || '',
      Operation: evt.Direction === 'Sent' ? 'SendMail' : 'ReceivedPhish',
    }));
}

/**
 * URL-click rows the timeline shows: clicks that reached the URL, narrowed to the
 * confirmed ones once the analyst has confirmed any.
 *
 * 🔴 Confirming one click IS the analyst saying the others are not the story -- the same
 * judgment a pill expresses, made through a classification instead. So the unconfirmed
 * clicks belong on the set-aside side of the count, not the incident side (8 of 9 on the
 * reference case). A click that never reached the URL is not an access at all.
 *
 * @param {Array<object>} urlClickEvents Filtered URL-click rows.
 * @param {Object<string,string>} urlClassifications Per-event classification map.
 * @returns {Array<object>} Tagged rows.
 */
export function selectTimelineUrlClicks(urlClickEvents, urlClassifications) {
  const cls = urlClassifications || {};
  const rows = urlClickEvents || [];
  const hasConfirmed = rows.some((evt) => cls[evt.EventId] === 'confirmed');
  return rows
    .filter((evt) => {
      if (evt.ActionType !== 'ClickAllowed') return false;
      if (hasConfirmed) return cls[evt.EventId] === 'confirmed';
      return true;
    })
    .map((evt) => ({
      ...evt,
      _eventType: 'UrlClick',
      _category: 'UrlClick',
      UserPrincipalName: evt.AccountUpn || '',
      Operation: evt.ActionType || 'UrlClick',
    }));
}

// ── Combine and tag all events ───────────────────────────────────────────────

/**
 * Combine all 14 event type arrays into a single sorted list, tagged with
 * _eventType, _description, _country, _vpnProxyFlags, _riskScore, _appName.
 *
 * This mirrors the combinedEvents + enrichedEvents logic from TimelineView.jsx.
 */
export function combineAndTagEvents({
  filteredResults,
  ipLookup,
  knownAppIdSet = new Set(),
  hideKnownApps = true,
  hideMsSubstrate = false,
  hideOutlookNoise = false,
  ipClassifications = {},
  // The two inputs the shared SELECTION rules need. Both default empty, which yields the
  // pre-v1.171.0 behaviour for a caller that has neither -- outbound mail only, and every
  // click that reached its URL.
  //
  // ⚠️ THE EXECUTIVE REPORT MUST PASS BOTH. Its pack decides `reviewedEventCount` from
  // this stream, so an omitted `urlClassifications` would count clicks the analyst has
  // already ruled out, and an omitted `phishingFlags` would drop the one inbound message
  // that IS the incident.
  phishingFlags = [],
  urlClassifications = {},
  targetUpn = '',
  maskSubjects = false,
  // Both default OFF, and the default is what the Executive Report's fact pack uses.
  //
  // `includeNonInteractive` adds the graph_signin_noninteractive feed. It is the feed
  // token replay / SSO reuse shows up in, and it was absent from every report's event
  // stream while being 60 rows on screen.
  //
  // `rollups` collapses sign-ins and non-interactive authentications into access periods
  // and directory rows into actions -- the shape All Events renders.
  //
  // 🔴 Neither may be turned on for a COUNTING surface. The fact pack derives metrics,
  // accessed items and mailbox detail from this stream, and the server bounds each metric
  // by the pinned run blob's own per-workload totals; a rolled stream would report 6
  // sign-ins where the run holds 620. See utils/allEventsRollups.js.
  includeNonInteractive = false,
  rollups = false,
}) {
  const fr = filteredResults;

  // Phase 1: tag event types (mirrors TimelineView combinedEvents useMemo)
  const signIns = (fr.SignInEvents || []).map(evt => ({ ...evt, _eventType: 'SignIn', _category: 'SignIn' }));

  const exchanges = (fr.ExchangeEvents || [])
    .filter(evt => !HIDDEN_EXCHANGE_OPS.has(evt.Operation))
    .map(evt => {
      const cat = getExchangeCategory(evt.Operation);
      return { ...evt, _eventType: cat === 'SharePoint' || cat === 'SharePointList' ? cat : 'Exchange', _category: cat };
    });

  const dirAudit = (fr.DirectoryAuditEvents || [])
    .filter(evt => !isAutoMfaUpdate(evt))
    .map(evt => ({ ...evt, _eventType: 'DirectoryAudit', _category: 'DirectoryAudit' }));

  const spEvents = (fr.SharePointEvents || [])
    .filter(evt => !HIDDEN_SHAREPOINT_OPS.has(evt.Operation))
    .map(evt => {
      const cat = getExchangeCategory(evt.Operation);
      return { ...evt, _eventType: cat === 'SharePoint' || cat === 'SharePointList' ? cat : 'SharePoint', _category: cat };
    });

  // Both via the SHARED selectors -- see their comments. These two lines used to be the
  // last place the report's stream and the screen's chose different rows.
  const msgTraces = selectTimelineMessageTrace(fr.MessageTraceEvents, phishingFlags);

  const urlClicks = selectTimelineUrlClicks(fr.UrlClickEvents, urlClassifications);

  const dlps = (fr.DlpEvents || []).filter(evt => !HIDDEN_DLP_OPS.has(evt.Operation)).map(evt => ({ ...evt, _eventType: 'DLP', _category: 'DLP' }));

  const generals = (fr.GeneralEvents || []).filter(evt => !HIDDEN_GENERAL_OPS.has(evt.Operation)).map(evt => ({ ...evt, _eventType: 'General', _category: 'General' }));

  const azureActs = (fr.AzureActivityEvents || []).map(evt => ({ ...evt, _eventType: 'AzureActivity', _category: 'AzureActivity' }));

  const riskDets = (fr.RiskDetectionEvents || []).map(evt => ({
    ...evt, _eventType: 'RiskDetection', _category: 'RiskDetection', Operation: evt.RiskEventType || 'RiskDetection',
  }));

  const riskyUsers = (fr.RiskyUserEvents || []).map(evt => ({
    ...evt, _eventType: 'RiskyUser', _category: 'RiskyUser', Operation: `Risk: ${evt.RiskLevel || 'Unknown'}`,
  }));

  const dedupedAlerts = deduplicateAlerts((fr.SecurityAlertEvents || []).filter(e => e.IsUserRelated));
  const secAlerts = [];
  for (const evt of dedupedAlerts) {
    const activityTime = evt.FirstActivityDateTime || evt.firstActivityDateTime || evt.RawLog?.firstActivityDateTime || '';
    const url = getSecurityAlertUrl(evt);
    const hasNonSignInEvidence = !!url; // URL evidence means user did something beyond sign-in

    // 1. Activity row — at the time the user action happened (only for non-sign-in evidence)
    if (hasNonSignInEvidence && activityTime) {
      secAlerts.push({
        ...evt,
        EventTimeUTC: activityTime,
        EventId: `${evt.EventId}_activity`,
        _eventType: 'AlertEvidence', _category: 'AlertEvidence',
        Operation: evt.Title || 'Alert',
        _alertUrl: url,
        _alertTitle: evt.Title || '',
        _alertSeverity: evt.Severity || '',
      });
    }

    // 2. Alert row — at the time Microsoft generated the alert
    secAlerts.push({
      ...evt,
      _eventType: 'SecurityAlert', _category: 'SecurityAlert',
      Operation: evt.Title || 'Alert',
    });
  }

  const secIncidents = (fr.SecurityIncidentEvents || []).filter(evt => evt.IsUserRelated).map(evt => ({
    ...evt, _eventType: 'SecurityIncident', _category: 'SecurityIncident', Operation: evt.DisplayName || 'Incident',
  }));

  // Tagged to match TimelineView exactly, INCLUDING `Operation: 'TokenRefresh'` -- the
  // row's own label in the source feed. The Application column shows the applications
  // instead (_niAppLabel, stamped by the roll-up), because that literal word printed
  // identically on all 2,836 rows and made the feed look content-free.
  const nonInteractives = includeNonInteractive
    ? (fr.NonInteractiveSignInEvents || []).map(evt => ({
        ...evt, _eventType: 'NonInteractiveSignIn', _category: 'NonInteractiveSignIn',
        Operation: 'TokenRefresh',
      }))
    : [];

  let combined = [
    ...signIns, ...exchanges, ...dirAudit, ...spEvents, ...msgTraces, ...urlClicks,
    ...dlps, ...generals, ...azureActs, ...riskDets, ...riskyUsers,
    ...secAlerts, ...secIncidents, ...nonInteractives,
  ];

  // Applied HERE -- after tagging, before enrichment and before the hide-pills -- which
  // is the same point TimelineView applies it. Enrichment must run on the ROLLED row or
  // a period's description would be its first constituent's.
  if (rollups) combined = applyAllEventsRollups(combined);

  // Phase 2: enrich with computed fields (mirrors AllEventsView enrichedEvents useMemo)
  const upn = targetUpn || combined.find(e => e.UserPrincipalName)?.UserPrincipalName || '';

  combined = combined.map(evt => {
    let desc = generateDescription(evt, { maskSubjects });
    // Strip UPN from descriptions
    if (upn) {
      const upnLower = upn.toLowerCase();
      const descLower = desc.toLowerCase();
      if (descLower.startsWith(upnLower + ' ')) {
        desc = desc.slice(upn.length + 1);
      } else {
        const idx = descLower.indexOf(` for ${upnLower}`);
        if (idx !== -1) {
          desc = desc.slice(0, idx) + desc.slice(idx + ` for ${upn}`.length);
        }
      }
    }
    return {
      ...evt,
      _description: desc,
      _country: getCountry(evt, ipLookup),
      _vpnProxyFlags: getVPNProxyFlags(evt, ipLookup),
      _riskScore: evt._eventType === 'SignIn'
        ? (evt.RiskScore ?? evt.Risk_Total ?? null)
        : (evt.IPAddress && ipLookup[evt.IPAddress]?.RiskLevel != null
            ? ipLookup[evt.IPAddress].RiskLevel
            : (!evt.IPAddress && evt.InferredIP && ipLookup[evt.InferredIP]?.RiskLevel != null
                ? ipLookup[evt.InferredIP].RiskLevel
                : null)),
      // ONE label function, shared with TimelineView (utils/allEventsRollups.js). On an
      // unrolled stream it returns exactly what this expression used to; on a rolled one it
      // names every application a period spans instead of only its first constituent's.
      _appName: rowAppLabel(evt),
      _appId: evt.ApplicationId || evt.ClientAppId || null,
    };
  });

  // Phase 3: filter known apps (preserving threat-flagged events and Directory Audit)
  if (hideKnownApps && knownAppIdSet.size > 0) {
    combined = combined.filter(evt => {
      // Directory Audit and Alert Evidence events are never filtered
      if (evt._eventType === 'DirectoryAudit' || evt._eventType === 'AlertEvidence') return true;
      if (!evt._appId || !knownAppIdSet.has(evt._appId)) return true;
      return mustShowDespiteImplicitTrust(evt, { ipClassifications, ipLookup });
    });
  }

  // Phase 3b: hide MS-substrate background reads (only the MS-Substrate class), unless a
  // threat signal forces them visible. Mirrors the timeline toggle so reports stay consistent.
  if (hideMsSubstrate) {
    combined = combined.filter(evt =>
      evt.AppTrustClass !== 'MS-Substrate' || mustShowDespiteImplicitTrust(evt, { ipClassifications, ipLookup })
    );
  }

  // Phase 3c: hide noisy Outlook background operations (OutlookItemPrioritized, etc.)
  // when the toggle is on. These carry no user/attacker signal, so no must-show
  // exception is needed. Mirrors the timeline toggle so reports stay consistent.
  if (hideOutlookNoise) {
    combined = combined.filter(evt => !OUTLOOK_NOISE_OPS.has(evt.Operation));
  }

  // Phase 4: sort by time ascending
  combined.sort((a, b) => {
    const ta = a.EventTimeUTC || '';
    const tb = b.EventTimeUTC || '';
    return ta < tb ? -1 : ta > tb ? 1 : 0;
  });

  return combined;
}
